import { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { AlertTriangle, Archive, Pencil, ChevronRight, Loader2 } from "lucide-react";

/**
 * Detects groups of dishes sharing a normalized (accent+case insensitive) name
 * and surfaces them as a dismissible banner. Click "Revisar" to open a dialog
 * showing each group and quickly archive the duplicates you don't want.
 *
 * Props:
 *   dishes           — current non-archived dishes list
 *   onOpenDish(dish) — open the RecipeDetailDialog to rename
 *   onArchiveDish    — async (dish) => void
 *   busyId           — id currently being archived
 */
function normalize(s) {
  return (s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().trim();
}

export default function DuplicatesBanner({ dishes, onOpenDish, onArchiveDish, busyId }) {
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => {
    const map = new Map();
    for (const d of dishes || []) {
      const key = normalize(d.name);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    return Array.from(map.values()).filter((g) => g.length >= 2);
  }, [dishes]);

  if (groups.length === 0) return null;

  const totalDupes = groups.reduce((acc, g) => acc + g.length, 0);

  return (
    <>
      <Card
        className="flex items-center gap-3 p-4 border-amber-200 bg-amber-50/60"
        data-testid="duplicates-banner"
      >
        <div className="w-9 h-9 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-950">
            {groups.length === 1
              ? "Se ha detectado 1 grupo de platos con nombre duplicado"
              : `Se han detectado ${groups.length} grupos de platos con nombres duplicados`}
          </div>
          <div className="text-xs text-amber-800/90 mt-0.5">
            {totalDupes} platos afectados — pueden crear confusión en el TPV y en las ventas.
          </div>
        </div>
        <Button
          size="sm"
          className="bg-amber-700 hover:bg-amber-800 text-white shrink-0"
          onClick={() => setOpen(true)}
          data-testid="duplicates-review-btn"
        >
          Revisar
          <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="duplicates-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              Platos con nombres duplicados
            </DialogTitle>
            <DialogDescription>
              Cada grupo contiene platos que comparten nombre (sin distinguir
              mayúsculas ni tildes). Renómbralos para diferenciarlos, o archiva
              el que no necesites.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-4 pr-1 -mr-1">
            {groups.map((group, idx) => (
              <div
                key={`group-${idx}`}
                className="rounded-xl border border-border bg-card overflow-hidden"
                data-testid={`duplicate-group-${idx}`}
              >
                <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {group.length} platos
                  </Badge>
                  <span className="text-sm font-medium text-foreground truncate">
                    «{group[0].name}»
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {group.map((dish) => (
                    <div
                      key={dish.dish_id}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/60 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {dish.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {Number(dish.price || 0).toFixed(2)} €
                          {dish.is_active === false && " · Inactivo"}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs"
                        onClick={() => {
                          setOpen(false);
                          onOpenDish?.(dish);
                        }}
                        data-testid={`duplicate-rename-${dish.dish_id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1.5" />
                        Renombrar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
                        disabled={busyId === dish.dish_id}
                        onClick={() => onArchiveDish?.(dish)}
                        data-testid={`duplicate-archive-${dish.dish_id}`}
                      >
                        {busyId === dish.dish_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Archive className="w-3 h-3 mr-1.5" />
                            Archivar
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
